import type { ISubscriptionBlockApiPort } from '@subscriptions/application/ports';
import type { GenerateReferenceMode } from '@subscriptions/domain/types/SubscriptionsConst';
import type {
  CreateSubscriptionBlockDto,
  UpdateBlockPaymentMethodDto,
  UpdateBlockInvoiceDataDto,
  UpdateBlockStoresDto,
  StoreViewDto,
  PaymentMethodViewDto,
} from '@subscriptions/application/dtos';
import type { SubscriptionBlock } from '@subscriptions/domain/entities';
import type { SubscriptionBlockHttpClientType } from '@subscriptions/infrastructure/api/SubscriptionBlockHttpClient';
import type { SubscriptionBlockApiMapperType } from '@subscriptions/infrastructure/mappers/SubscriptionBlockApiMapper';

/**
 * Creates the subscription block repository implementing ISubscriptionBlockApiPort.
 * Orchestrates HTTP client and mapper to fulfill application contracts.
 */
export const createSubscriptionBlockApiRepository = (props: {
  httpClient: SubscriptionBlockHttpClientType;
  mapper: SubscriptionBlockApiMapperType;
}): ISubscriptionBlockApiPort => {
  const { httpClient, mapper } = props;

  const list = async (): Promise<SubscriptionBlock[]> => {
    const apiResponse = await httpClient.list();
    return apiResponse.map(mapper.mapApiResponseToEntity);
  };

  const getById = async (blockUuid: string): Promise<SubscriptionBlock> => {
    const apiResponse = await httpClient.getById(blockUuid);
    return mapper.mapApiResponseToEntity(apiResponse);
  };

  const create = async (dto: CreateSubscriptionBlockDto): Promise<SubscriptionBlock> => {
    const apiRequest = mapper.mapCreateDtoToApiRequest(dto);
    const apiResponse = await httpClient.create(apiRequest);
    return mapper.mapApiResponseToEntity(apiResponse);
  };

  const updatePaymentMethod = async (
    blockUuid: string,
    dto: UpdateBlockPaymentMethodDto,
  ): Promise<SubscriptionBlock> => {
    const apiRequest = mapper.mapUpdatePaymentMethodDtoToApiRequest(dto);
    const apiResponse = await httpClient.updatePaymentMethod(blockUuid, apiRequest);
    return mapper.mapApiResponseToEntity(apiResponse);
  };

  const updateInvoiceData = async (blockUuid: string, dto: UpdateBlockInvoiceDataDto): Promise<SubscriptionBlock> => {
    const apiRequest = mapper.mapUpdateInvoiceDataDtoToApiRequest(dto);
    const apiResponse = await httpClient.updateInvoiceData(blockUuid, apiRequest);
    return mapper.mapApiResponseToEntity(apiResponse);
  };

  const updateStores = async (blockUuid: string, dto: UpdateBlockStoresDto): Promise<{ uuid: string }> => {
    const apiRequest = mapper.mapUpdateStoresDtoToApiRequest(dto);
    const apiResponse = await httpClient.updateStores(blockUuid, apiRequest);
    return { uuid: apiResponse.uuid };
  };

  const deleteFn = async (blockUuid: string): Promise<void> => {
    await httpClient.delete(blockUuid);
  };

  const listStores = async (blockUuid?: string): Promise<StoreViewDto[]> => {
    const apiResponse = await httpClient.listStores(blockUuid);
    return mapper.mapStoresApiToViewDtos(apiResponse);
  };

  const generateReference = async (blockUuid: string, mode?: GenerateReferenceMode): Promise<PaymentMethodViewDto> => {
    const apiResponse = await httpClient.generateReference(blockUuid, mode);
    const paymentMethod = mapper.mapPaymentMethodApiResponseToViewDto(apiResponse);
    // The reactivation CLABE carries the amount quoted for today.
    if (apiResponse.breakdown && apiResponse.clabeValidUntil) {
      return {
        ...paymentMethod,
        reactivation: {
          total: apiResponse.breakdown.total,
          quotedOn: apiResponse.breakdown.quotedOn,
          clabeValidUntil: apiResponse.clabeValidUntil,
        },
      };
    }
    return paymentMethod;
  };

  const attachPaymentMethod = async (blockUuid: string, paymentMethodUuid: string): Promise<PaymentMethodViewDto> => {
    const apiResponse = await httpClient.attachPaymentMethod(blockUuid, paymentMethodUuid);
    return mapper.mapPaymentMethodApiResponseToViewDto(apiResponse);
  };

  return {
    create,
    list,
    getById,
    updatePaymentMethod,
    updateInvoiceData,
    updateStores,
    delete: deleteFn,
    listStores,
    generateReference,
    attachPaymentMethod,
  };
};
